/**
 * Turn API error responses into short, human-centred messages.
 * Never show raw JSON, constraint names, or stack traces to users.
 */
export function getApiErrorMessage(errorTextOrBody, fallback = 'Something went wrong. Please try again.') {
  if (errorTextOrBody == null || errorTextOrBody === '') return fallback;

  let payload = errorTextOrBody;
  if (typeof errorTextOrBody === 'string') {
    const trimmed = errorTextOrBody.trim();
    if (!trimmed) return fallback;
    try {
      payload = JSON.parse(trimmed);
    } catch (_) {
      // Plain text — strip obvious technical noise
      if (/duplicate key|violates unique constraint|tenant_site_id|ECONNREFUSED|Internal Server Error/i.test(trimmed)) {
        return fallback;
      }
      return trimmed.length > 280 ? fallback : trimmed;
    }
  }

  if (typeof payload !== 'object' || payload === null) return fallback;

  const code = payload.code;
  const error = typeof payload.error === 'string' ? payload.error : '';
  const rawMessage = payload.message;
  const message = typeof rawMessage === 'string'
    ? rawMessage
    : Array.isArray(rawMessage) && rawMessage.length > 0
      ? String(rawMessage[0])
      : '';
  const combined = `${error} ${message}`;

  if (/access to this application for this tenant/i.test(combined)) {
    return 'Flow is not available for that organisation yet. Create an account for it, or pick an organisation you already use.';
  }
  if (/application enabled/i.test(combined)) {
    return 'This organisation does not have Flow enabled. Please contact your administrator.';
  }

  switch (code) {
    case 'TENANT_SITE_REQUIRED':
      return error || 'This record needs a site before you can continue. Link a site, or create a new service plan with a site selected.';
    case 'SERVICE_PLAN_REMOVED':
      return error || 'This service plan has been removed, so that action isn’t available.';
    case 'QUOTATION_REMOVED':
      return error || 'This quotation has been removed, so that action isn’t available.';
    case 'PRICE_ITEM_NOT_ON_SITE':
      return error || 'That price item isn’t available on this site’s price list.';
    case 'DUPLICATE_CARE_PLAN_ID':
    case 'DUPLICATE_CARE_PLAN_ACTIVITY':
    case 'DUPLICATE_RECORD':
      return error || 'We couldn’t save this because of a conflict. Please try again.';
    case 'RELATED_RECORD_MISSING':
      return error || 'Something this depends on is missing. Refresh the page and try again.';
    case 'MISSING_REQUIRED_FIELD':
      return error || 'Some required information is missing. Please check your entries and try again.';
    default:
      break;
  }

  if (error) {
    if (/duplicate key|violates unique constraint|tenant_site_id|status 2|ECONNREFUSED/i.test(error)) {
      if (/tenant_site/i.test(error)) {
        return 'This quotation or service plan isn’t linked to a site yet. Create a new service plan with a site selected, then try again.';
      }
      if (/duplicate key|unique constraint/i.test(error)) {
        return 'We couldn’t save this because of a temporary conflict. Please try again.';
      }
      if (/status 2|removed/i.test(error)) {
        return 'This service plan has been removed, so that action isn’t available.';
      }
      return fallback;
    }
    return error;
  }

  if (message) {
    return message.length > 280 ? fallback : message;
  }

  return fallback;
}

export async function readApiErrorMessage(response, fallback) {
  try {
    const text = await response.text();
    return getApiErrorMessage(text, fallback);
  } catch (_) {
    return fallback || 'Something went wrong. Please try again.';
  }
}
